import React, { useEffect } from 'react';
import styles from './AutoShutoffModal.module.css';
import { FloModal, FloModalBodyProps, FloCheckbox, TagList } from 'components';
import { Trans, useTranslation } from 'react-i18next';
import Message from 'containers/common/Message';
import { Col, Row } from 'react-bootstrap';
import { Location } from 'model';
import ContentLoader from "react-content-loader";
import { ThresholdSettings } from 'model/Threshold';

interface AutoShutoffModalBodyProps extends FloModalBodyProps {
  locations:   Location[];
  thresholds:  ThresholdSettings;
  onChange:    (thresholds: ThresholdSettings) => void;
  onSave:      (locationIds: string[], settings: ThresholdSettings) => void;
}

const AutoShutoffModalBody: React.FC<AutoShutoffModalBodyProps> = React.memo(({
  locations,
  thresholds,
  onChange,
  onSave,
  bindSubmitForm,
}) => {
  const { t } = useTranslation();

  bindSubmitForm(async () => {
    onSave(locations.map(l => l.id as string), thresholds);
  });

  return (
    <>
      <Row className={styles.bodyContainer}>
        <Col xs="12">
          <Row>
            <Col xs="12">
              <Message />
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <div>
                <div className={styles.locationInfo}>
                  <Trans
                    i18nKey="set-thresholds-location-info-title"
                    count={locations.length}
                    components={{ span: <span /> }}
                  />
                </div>
                <TagList
                  list = {locations.map(l => l.nickname || l.address || '')}
                  max = {7}
                />
              </div>
              <div className={styles.shutoffGroup} role="radiogroup" aria-label={t('Auto Shutoff')}>
                <span className={styles.shutoffLabel}>{t('Auto Shutoff')}:</span>
                <div
                  className={styles.shutoffOption}
                  role="radio"
                  aria-checked={!thresholds.shutoffDisabled}
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onChange({ ...thresholds, shutoffDisabled: false }); }}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange({ ...thresholds, shutoffDisabled: false }); } }}
                >
                  <FloCheckbox
                    checked={!thresholds.shutoffDisabled}
                    onChange={() => onChange({ ...thresholds, shutoffDisabled: false })}
                  />
                  <span className={styles.shutoffOptionLabel}>{t('Enabled')}</span>
                </div>
                <div
                  className={styles.shutoffOption}
                  role="radio"
                  aria-checked={thresholds.shutoffDisabled}
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onChange({ ...thresholds, shutoffDisabled: true }); }}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange({ ...thresholds, shutoffDisabled: true }); } }}
                >
                  <FloCheckbox
                    checked={thresholds.shutoffDisabled}
                    onChange={() => onChange({ ...thresholds, shutoffDisabled: true })}
                  />
                  <span className={styles.shutoffOptionLabel}>{t('Disabled')}</span>
                </div>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
})

const AutoShutoffSkeleton: React.FC<{}> = () => (
  <ContentLoader
    height={150}
    width={'100%'}
    speed={3}
  >
    <rect x="0" y="0" rx="3" ry="3" width="200" height="20" />
    <rect x="0" y="50" rx="3" ry="3" width="80" height="24" />
    <rect x="90" y="50" rx="3" ry="3" width="110" height="24" />
    <rect x="210" y="50" rx="3" ry="3" width="60" height="24" />
    <rect x="0" y="100" rx="3" ry="3" width="300" height="30" />
  </ContentLoader>
)

type AutoShutoffModalProps = {
  show:                     boolean,
  onHide:                   () => void,
  isSubmitting:             boolean,
  locations:                Location[],
  thresholds:               ThresholdSettings | null,
  fetchThresholdSettings:   (locations: Location[]) => void,
  updateThresholdsSettings: (settings: ThresholdSettings) => void,
  saveThresholdSettings:    (locationIds: string[], settings: ThresholdSettings) => void,
  clearMessage:             () => void,
  hasError:                 boolean,
}

export const AutoShutoffModal: React.FC<AutoShutoffModalProps> = React.memo(({
  show,
  locations,
  thresholds,
  fetchThresholdSettings,
  updateThresholdsSettings,
  saveThresholdSettings,
  clearMessage,
  hasError,
  ...rest
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (show) {
      clearMessage();
      fetchThresholdSettings(locations);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <FloModal
      {...rest}
      show            = {show}
      name            = "auto-shutoff-modal"
      title           = {t('Auto Shutoff')}
      className       = {styles.root}
      disableButtons  = {thresholds === null || rest.isSubmitting}
      body            = { (props) => thresholds ?
        <AutoShutoffModalBody
          {...props}
          locations   = {locations}
          thresholds  = {thresholds}
          onChange    = {thresholds => updateThresholdsSettings(thresholds)}
          onSave      = {saveThresholdSettings}
        />
        :
        <AutoShutoffSkeleton />
      }
    />
  )
})
